import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'

  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useI18n from '@/composables/useI18n'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import RadioButton from '@/components/common/RadioButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import ExpandableTextAreaInput from '@/components/common/ExpandableTextAreaInput.vue'
  import OnboardingSurveyAnswerResponseView from '@/components/onboarding-survey/OnboardingSurveyAnswerResponseView.vue'
  // @ts-ignore
  import DatePicker from 'vue2-datepicker'

  import type { PropType } from 'vue'
  import type {
    OnboardingSurveyAnswerResponseData,
    CompanyOnboardingSurveyQuestion,
    OnboardingSurveyQuestionAnswer,
    OnboardingSurveyQuestionResponse,
  } from '@/services/interfaces/Auth0'
  import type { CourseVertical } from '@/services/interfaces/Course'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'OnboardingSurveyQuestion',
  props: {
    lockedTrialBundle: {
      type: String as PropType<CourseVertical | null>,
      default: null,
    },
    question: {
      type: Object as PropType<CompanyOnboardingSurveyQuestion>,
      required: true,
    },
    questionIndex: {
      type: Number,
      required: true,
    },
    totalQuestions: {
      type: Number,
      required: true,
    },
    questionResponse: {
      type: Object as PropType<OnboardingSurveyQuestionResponse>,
      default: null,
    },
    loadingNextStep: {
      type: Boolean,
      default: false,
    },
    transitionName: {
      type: String,
      default: 'next-slide',
    },
    isLastQuestion: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'set-question-response',
    'next-question-or-complete-survey',
    'previous-question',
    'custom-text-answer',
    'exam-date',
  ],
  setup(__props, { emit }) {

const props = __props;

  const { course34iIcon, course34dIcon, course34fIcon, arrowRightIndicator } = useIcons()
  const { extractCourseVertical, disableDateBeforeToday } = useCommonMixin()
  const { translateString } = useI18n()
  const { isMobileDevice } = useBreakpoint()

  

  

  const customTextAnswer = ref(props.questionResponse?.customText ?? '')
  const examDate = ref(props.questionResponse?.examDate ?? '')

  const questionResponse = computed(() => props.questionResponse)

  const isGeneralQuestion = computed(() => props.question?.courseVertical === 'General')

  const courseVerticalAnswerIcon = computed(() => (answerText: string) => {
    const vertical = extractCourseVertical(answerText)
    if (vertical === '34i') return course34iIcon.value
    if (vertical === '34d') return course34dIcon.value
    return course34fIcon.value
  })

  const answerIsSelected = computed(() => (answerId: string) => {
    if (!questionResponse.value) {
      return false
    }

    const { selectedAnswerIds } = questionResponse.value
    return selectedAnswerIds.length > 0 && selectedAnswerIds.includes(answerId)
  })

  const loading = computed(() => props.loadingNextStep)

  const nextOrCompleteCTADisabled = computed(() => {
    if (!questionResponse.value) return true
    return questionResponse.value.selectedAnswerIds.length === 0
  })

  const isCustomAnswerInput = computed(
    () => (answer: OnboardingSurveyQuestionAnswer) =>
      answer.actionType === 'save_custom_text' || answer.actionType === 'save_exam_date',
  )

  const selectedAnswerWithResponseData = computed(() => {
    return props.question?.answers.find(
      (ans) => answerIsSelected.value(ans.id) && ans.responseEmoji && ans.responseText,
    )
  })

  const answerResponseData = computed((): OnboardingSurveyAnswerResponseData => {
    return {
      responseEmoji: selectedAnswerWithResponseData.value?.responseEmoji ?? '',
      responseTitle: selectedAnswerWithResponseData.value?.responseTitle ?? '',
      responseText: selectedAnswerWithResponseData.value?.responseText ?? '',
    }
  })

  const questionProgress = computed(() => {
    return `${props.questionIndex + 1} ` + translateString('of') + ` ${props.totalQuestions}`
  })

  const handleSetAnswer = (answer: OnboardingSurveyQuestionAnswer) => {
    customTextAnswer.value = ''
    examDate.value = ''
    selectAnswer(answer)
  }

  const selectAnswer = (answer: OnboardingSurveyQuestionAnswer) => {
    emit('set-question-response', {
      questionId: props.question.id,
      questionType: props.question.questionType,
      answer: answer,
      answerWasSelectedBefore: answerIsSelected.value(answer.id),
    })
  }

  const setExamDateAnswer = (answer: OnboardingSurveyQuestionAnswer, date: string) => {
    setCustomAnswer(answer, date)

    examDate.value = date
  }

  const setCustomTextAnswer = (answer: OnboardingSurveyQuestionAnswer, text: string) => {
    setCustomAnswer(answer, text)

    customTextAnswer.value = text || ''
    emit('custom-text-answer', text)
  }

  const setCustomAnswer = (answer: OnboardingSurveyQuestionAnswer, text: string) => {
    const isAnswerSelected = answerIsSelected.value(answer.id)

    if (text) {
      if (!isAnswerSelected) selectAnswer(answer)
    } else {
      selectAnswer(answer)
    }
  }

  const nextStep = () => {
    if (nextOrCompleteCTADisabled.value) return

    emit('next-question-or-complete-survey')
  }

  const previousStep = () => {
    if (selectedAnswerWithResponseData.value) {
      selectAnswer(selectedAnswerWithResponseData.value)
    } else {
      emit('previous-question')
    }
  }

  watch(
    () => examDate.value,
    (value) => {
      emit('exam-date', value)
    },
  )

return { __sfc: true,course34iIcon, course34dIcon, course34fIcon, arrowRightIndicator, extractCourseVertical, disableDateBeforeToday, translateString, isMobileDevice, props, emit, customTextAnswer, examDate, questionResponse, isGeneralQuestion, courseVerticalAnswerIcon, answerIsSelected, loading, nextOrCompleteCTADisabled, isCustomAnswerInput, selectedAnswerWithResponseData, answerResponseData, questionProgress, handleSetAnswer, selectAnswer, setExamDateAnswer, setCustomTextAnswer, setCustomAnswer, nextStep, previousStep, computed, ref, watch, useIcons, useCommonMixin, useBreakpoint, useI18n, KetchUpButton, SVGRenderer, RadioButton, LoadingSpinner, ExpandableTextAreaInput, OnboardingSurveyAnswerResponseView, DatePicker }
}

})